import { useState, useCallback, useEffect } from 'react';
import { createRequest } from '../services/requestService';
import { getAllEvents } from '../services/eventService';

/**
 * useRequestForm hook
 * Handles event request submission state, dynamic event database retrieval,
 * "not clear yet" date toggle logic, and payload formatting.
 */
export function useRequestForm() {
  const [form, setForm] = useState({
    fullName: '',
    phone: '',
    text: '',
    isRead: false,
    isEventDateClear: true, // true means date is clear (decided); false means not clear yet
    eventDate: '',
    eventIds: [], // List of selected Event entity IDs
  });

  const [dbEvents, setDbEvents] = useState([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);

  const [errors, setErrors] = useState({});
  const [globalError, setGlobalError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Fetch db-driven events list on mount
  useEffect(() => {
    async function loadEvents() {
      setIsLoadingEvents(true);
      try {
        const list = await getAllEvents();
        setDbEvents(list);
      } catch (err) {
        console.warn('Could not load events from DB (might be protected). Using backup static events.');
        // Fallback static events
        setDbEvents([
          { id: 1, title: "Nikoh to'yi" },
          { id: 2, title: "Tug'ilgan kun" },
          { id: 3, title: "Korporativ" },
          { id: 4, title: "Sunnat to'yi" },
          { id: 5, title: "Beshik to'yi" },
          { id: 6, title: "Fotiha / Unashtiruv" },
          { id: 7, title: "Yubiley" },
        ]);
      } finally {
        setIsLoadingEvents(false);
      }
    }
    loadEvents();
  }, []);

  /** Toggle Event ID in selected list */
  const handleToggleEvent = useCallback((eventId) => {
    setForm((prev) => {
      const selected = prev.eventIds.includes(eventId)
        ? prev.eventIds.filter((id) => id !== eventId)
        : [...prev.eventIds, eventId];

      // Clear field error on select change
      setErrors((errs) => ({ ...errs, eventIds: '' }));
      return { ...prev, eventIds: selected };
    });
  }, []);

  /** Toggle Date Clear state */
  const handleToggleDateClear = useCallback((e) => {
    const isChecked = e.target.checked; // "Sana hali aniq emas" checkbox
    setForm((prev) => ({
      ...prev,
      isEventDateClear: !isChecked, // if "not clear yet" is checked, isEventDateClear is false
      eventDate: isChecked ? '' : prev.eventDate, // clear date if not clear yet
    }));
    // Clear date field error when toggling
    setErrors((errs) => ({ ...errs, eventDate: '' }));
  }, []);

  /** Validate all fields and return a map of field → error message. */
  const validate = (values) => {
    const e = {};
    if (!values.fullName.trim()) {
      e.fullName = 'Ism familiya kiritilishi shart.';
    }
    if (!values.phone.trim()) {
      e.phone = 'Telefon raqam kiritilishi shart.';
    } else if (values.phone.replace(/\D/g, '').length < 9) {
      e.phone = 'Telefon raqam kamida 9 ta raqamdan iborat bo\'lishi kerak.';
    }
    if (!values.text.trim()) {
      e.text = 'Xabar / batafsil ma\'lumot kiritilishi shart.';
    }
    if (values.isEventDateClear && !values.eventDate) {
      e.eventDate = 'Sana tanlanishi shart (yoki aniq emasligini belgilang).';
    }
    if (values.eventIds.length === 0) {
      e.eventIds = 'Kamida bitta tadbir turini tanlashingiz lozim.';
    }
    return e;
  };

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: '' } : prev));
    setGlobalError('');
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      const validationErrors = validate(form);
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      setIsLoading(true);
      setGlobalError('');

      try {
        const payload = {
          fullName: form.fullName.trim(),
          phone: form.phone.trim(),
          text: form.text.trim(),
          isRead: false,
          isEventDateClear: form.isEventDateClear,
          eventDate: form.isEventDateClear ? form.eventDate : null,
          eventIds: form.eventIds,
        };

        await createRequest(payload);
        setIsSuccess(true);

        // Reset form
        setForm({
          fullName: '',
          phone: '',
          text: '',
          isRead: false,
          isEventDateClear: true,
          eventDate: '',
          eventIds: [],
        });
      } catch (err) {
        console.error(err);
        const serverMsg = err?.response?.data?.message || err?.response?.data;
        if (!err.response) {
          setGlobalError('Serverga ulanib bo\'lmadi. Internet aloqangizni tekshiring.');
        } else {
          setGlobalError(typeof serverMsg === 'string' ? serverMsg : 'So\'rov yuborishda xatolik. Qayta tekshiring.');
        }
      } finally {
        setIsLoading(false);
      }
    },
    [form]
  );

  const resetSuccess = useCallback(() => {
    setIsSuccess(false);
  }, []);

  return {
    form,
    dbEvents,
    isLoadingEvents,
    errors,
    globalError,
    isLoading,
    isSuccess,
    handleChange,
    handleToggleEvent,
    handleToggleDateClear,
    handleSubmit,
    resetSuccess,
  };
}
